import classnames from "classnames";
import {
  Dispatch,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { OverlayPanel } from "primereact/overlaypanel";
import { Spacer } from "../../../components/Layout/Layout";
import {
  BaseFiltersResponse,
  FilterData,
} from "../../../core/api/common_table";
import useTranslations from "../../../core/i18n/useTranslations";
import {
  filterListToMap,
  mapToFilterList,
  validateFilterList,
} from "../../utils/customFilters";
import Button from "../Button/Button";
import Divider from "../Divider/Divider";
import Dropdown from "../Dropdown/Dropdown";
import { IDropdownItem } from "../Dropdown/DropdownItem";
import Icon from "../Icon";
import TextInput from "../Inputs/TextInput";
import { CustomFilterLinkedObjects } from "./CustomFilterLinkedObjects";
import {
  getFilterList,
  MoreState,
  toMoreState,
  useMoreFilter,
} from "../../../core/hooks/filters/useMoreFilter";
import { ClearFilter } from "./CleatFilters";
import { AutoDeactivatedAlarmsCheck } from "./AutoDeactivatedAlarmsCheck";
import { AutoDeactivatedFilter } from "../../../core/hooks/filters/useAutoDeactivatedFilter";
import { CustomAbsenceFilter } from "./CustomAbsenceFilter";
import useUser from "../../../core/user/useUser";
import { useFocusTrap } from "../../utils/useFocusTrap";
import PrimeTooltip from "../PrimeTooltip/PrimeTooltip";

import styles from "./Table.module.css";

// Filter of this type requires special handling
export const LINKED_OBJECT_ID = 8;
export const ABSENCE_ON_OBJECT = 9;

export declare type CustomFiltersPopupActions = {
  open: () => void;
  close: () => void;
  toggle: () => void;
};

export interface Filter {
  columnId?: number;
  filterConditions?: IDropdownItem;
  value?: any;
  id: string;
  errors?: ("columnName" | "filterConditions" | "value")[];
}

export interface CustomFiltersProps {
  filterColumns: BaseFiltersResponse;
  filters?: FilterData[];
  notAvailableFilters: FilterData[];
  maxFiltersAmount?: number;
  applyFilters: (data: FilterData[]) => void;
  translate: (col: { keyName: string; name: string }) => string;
  tableState?: any;
  setUnsavedCustomFilters?: Dispatch<SetStateAction<MoreState>>;
  autoDeactivatedFilter?: AutoDeactivatedFilter;
  setAutoDeactivatedFilter?: (args: AutoDeactivatedFilter) => void;
  resetPageFilter: () => void;
  disableDropdowns?: boolean;
  disableFilters?: number[];
}

/** Popup that should appear when table rows seleted */
export const CustomFilters = ({
  filterColumns,
  filters,
  notAvailableFilters,
  maxFiltersAmount = 3,
  applyFilters,
  translate,
  tableState,
  setUnsavedCustomFilters,
  autoDeactivatedFilter,
  setAutoDeactivatedFilter,
  resetPageFilter,
  disableDropdowns,
  disableFilters = [],
}: CustomFiltersProps) => {
  const t = useTranslations();
  const { config } = useUser();
  const { setMoreFilter, resetMoreFilter } = useMoreFilter(tableState);
  const customFiltersOverlayRef = useRef<OverlayPanel>(null);

  const {
    containerRef: popupBodyRef,
    triggerRef: triggerButtonRef,
    onKeyDown: handleKeyDown,
    onShow: handlePopupShow,
    onHide: handlePopupHide,
  } = useFocusTrap();

  const [filterItems, setFilterItems] = useState<Map<string, Filter>>(
    new Map()
  );
  const [initialFilters, setInitialFilters] = useState<Map<string, Filter>>(
    new Map()
  );
  const [newlyAddedFilterId, setNewlyAddedFilterId] = useState<string | null>(
    null
  );
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const userAbsencePermission = config?.show.includes("UserAbsence");

  useEffect(() => {
    let preDefinedFilters: Map<string, Filter> = new Map();
    if (filters) {
      preDefinedFilters = filterListToMap(filters);
    }
    if (tableState) {
      preDefinedFilters = filterListToMap(getFilterList(tableState));
    }
    setFilterItems(preDefinedFilters);
    setInitialFilters(preDefinedFilters);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(preDefinedFilters)));
    applyFilters(mapToFilterList(preDefinedFilters));
  }, []);

  useEffect(() => {
    // Reset when filters are cleared
    if (!filters) {
      setFilterItems(new Map());
      setUnsavedCustomFilters?.({});
    }
  }, [filters]);

  useEffect(() => {
    if (!newlyAddedFilterId) return;

    const filterId = newlyAddedFilterId;
    const focusButton = (): boolean => {
      let button = dropdownButtonRefs.current.get(filterId);

      if (!button) {
        const container = document.getElementById(`dropdown-${filterId}`);
        button = container?.querySelector(
          'button[type="button"]'
        ) as HTMLButtonElement;
        if (button) {
          dropdownButtonRefs.current.set(filterId, button);
        }
      }

      if (button) {
        button.focus();
        return true;
      }
      return false;
    };

    requestAnimationFrame(() => {
      if (!focusButton()) {
        setTimeout(() => focusButton(), 50);
      }
      setNewlyAddedFilterId(null);
    });
  }, [newlyAddedFilterId]);

  const numInlineFilterItems = useMemo(
    () =>
      Array.from(filterItems).filter(
        ([_, item]) =>
          item.columnId &&
          +item.columnId !== LINKED_OBJECT_ID &&
          +item.columnId !== ABSENCE_ON_OBJECT
      ).length,
    [filterItems]
  );

  const filterColumnsItems = useMemo(
    () =>
      filterColumns.map((col) => {
        const notAvailable = !!notAvailableFilters.find(
          (el) => col.id === el.id
        );

        const alreadySelected = !!Array.from(filterItems).find(
          ([, item]) => item.columnId && col.id === +item.columnId
        );

        const isInSelectedFilters = filters?.find((el) => col.id === el.id);

        return {
          name: translate(col),
          id: col.id,
          isSelected: false,
          disabled: (notAvailable && !isInSelectedFilters) || alreadySelected,
        };
      }),
    [filterColumns, notAvailableFilters, filterItems]
  );

  const showRows = () =>
    !!filterItems.size || autoDeactivatedFilter?.hideautodeactivated;

  const addFilter = useCallback(() => {
    const crypto = window.crypto;
    const array = new Uint32Array(1);
    const newId = crypto.getRandomValues(array)[0].toString();

    const newItem = {
      id: newId,
      columnId: filterColumnsItems.find((i) => !i.disabled)?.id,
    };

    filterItems.set(newId, newItem);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(filterItems)));
    setNewlyAddedFilterId(newId);
    return setFilterItems(new Map(filterItems));
  }, [filterItems, filterColumnsItems]);

  const onApplyFilters = useCallback(() => {
    const { updatedFilters, hasErrors } = validateFilterList(filterItems);
    if (hasErrors) {
      setFilterItems(updatedFilters);
    } else {
      const filterList = mapToFilterList(filterItems);
      setInitialFilters(filterItems);
      setMoreFilter(filterList);
      applyFilters(filterList);
      setUnsavedCustomFilters?.(toMoreState(filterList));
      customFiltersOverlayRef.current?.hide();
      resetPageFilter();
    }
  }, [filterItems, applyFilters]);

  const onCancel = () => {
    setFilterItems(filterListToMap(filters || []));
    setUnsavedCustomFilters?.(toMoreState(filters || []));
    customFiltersOverlayRef.current?.hide();
  };

  const handleRemoveFilter = (item: Filter) => {
    const updatedFilterMap = new Map(filterItems);
    updatedFilterMap.delete(item.id);

    setFilterItems(updatedFilterMap);
    setUnsavedCustomFilters?.(toMoreState(mapToFilterList(updatedFilterMap)));
  };

  const triggerButton = () => {
    const filterItemsSizeShifter = autoDeactivatedFilter?.hideautodeactivated
      ? 1
      : 0;
    const manyFilters = filterItems.size + filterItemsSizeShifter;

    return (
      <div className={styles.customFilterTrigger}>
        {showRows() ? (
          <Button
            text={`${filterItems.size + filterItemsSizeShifter} ${
              manyFilters > 1
                ? t("Common:custom_filters_many")
                : t("Common:custom_filters_single")
            }`}
            image="filter"
            variant="active"
            onClick={(e) => {
              triggerButtonRef.current = e.currentTarget;
              customFiltersOverlayRef.current?.toggle(e);
            }}
            dataCy="more_filters_btn"
          />
        ) : (
          <Button
            text={t("Common:custom_filters_more")}
            image="filter"
            variant="secondary"
            onClick={(e) => {
              triggerButtonRef.current = e.currentTarget;
              customFiltersOverlayRef.current?.toggle(e);
            }}
            dataCy="more_filters_btn"
          />
        )}
      </div>
    );
  };

  const renderFilterLine = (item: Filter) => {
    if (item.columnId) {
      if (
        +item.columnId === LINKED_OBJECT_ID ||
        +item.columnId === ABSENCE_ON_OBJECT
      ) {
        return null;
      }

      const items = filterColumnsItems.map((i) => ({
        ...i,
        isSelected: i.id === parseInt(String(item?.columnId ?? -1), 10),
      }));

      const selectedItem = items.find((i) => i.isSelected);

      const handleEnterPress = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter") {
          onApplyFilters();
        }
      };
      const uniqId = uuidv4();

      return (
        <div key={item.id} className={styles.filterRow}>
          <Dropdown
            title={t("Common:custom_filters_column_name")}
            id={`dropdown-${item.id}`}
            width={200}
            items={filterColumnsItems
              .filter((i) => i.id !== 8 && i.id !== 9)
              .map((i) => ({
                ...i,
                isSelected: i.id === item?.columnId,
              }))}
            placeholder=""
            dropdownInPopup
            selectedItem={selectedItem}
            invalid={item.errors?.includes("columnName")}
            onSelectItem={({ id }) => {
              setFilterItems((filtersMap) => {
                item.columnId = id as number;
                item.filterConditions = undefined;
                filtersMap.set(item.id, item);
                setUnsavedCustomFilters?.(
                  toMoreState(mapToFilterList(filtersMap))
                );
                return new Map(filtersMap);
              });
            }}
          />
          <TextInput
            required
            className={styles.filterValue}
            label={t("Common:custom_filters_column_value")}
            value={item.value as string}
            invalid={item.errors?.includes("value")}
            onKeyDown={handleEnterPress}
            onChange={(e) => {
              setFilterItems((filtersMap) => {
                item.value = e.target.value;
                item.errors = undefined;
                filtersMap.set(item.id, item);
                setUnsavedCustomFilters?.(
                  toMoreState(mapToFilterList(filtersMap))
                );
                return new Map(filtersMap);
              });
            }}
            validationError={
              item.errors?.includes("value")
                ? t("Errors:input_field_required")
                : undefined
            }
          />
          <button
            type="button"
            data-pr-at="right center-6"
            id={`customFilter-${uniqId}`}
            aria-label="Remove filter"
            className={styles.filterRemoveButton}
            onClick={() => handleRemoveFilter(item)}
            onKeyUp={(event: KeyboardEvent) => {
              if (event.key === " ") {
                handleRemoveFilter(item);
              }
            }}
            tabIndex={0}
          >
            <Icon name="x" />
          </button>
          <PrimeTooltip
            target={`#customFilter-${uniqId}`}
            position="right"
            content={t("Objects:remove_from")}
          />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {triggerButton()}
      <OverlayPanel
        ref={customFiltersOverlayRef}
        className={classnames({
          customFilters: true,
        })}
        onShow={handlePopupShow}
        onHide={handlePopupHide}
      >
        <section
          ref={popupBodyRef}
          className={styles.popupBody}
          data-cy="more_filters_popup"
          onKeyDown={handleKeyDown}
          aria-label={t("Common:custom_filters_more")}
        >
          <div className={styles.customFiltersBlock}>
            {filterColumnsItems.find((i) => +i.id === LINKED_OBJECT_ID) &&
              !disableFilters.includes(LINKED_OBJECT_ID) && (
                <CustomFilterLinkedObjects
                  item={{
                    id: "linked-objects",
                    columnId: filterColumnsItems.find(
                      (i) => +i.id === LINKED_OBJECT_ID
                    )?.id,
                    // @ts-ignore
                    value: mapToFilterList(filterItems).find(
                      (item) => item.id && +item.id === LINKED_OBJECT_ID
                    )?.value,
                  }}
                  onChange={(item) => {
                    setFilterItems((filtersMap) => {
                      let list = mapToFilterList(filtersMap);
                      list = list.filter(
                        (listItem) => +listItem.id !== LINKED_OBJECT_ID
                      );
                      const newFiltersMap = filterListToMap(list);
                      if (!item.value) {
                        newFiltersMap.delete(item.id);
                      } else {
                        newFiltersMap.set(item.id, item);
                      }
                      setUnsavedCustomFilters?.(
                        toMoreState(mapToFilterList(newFiltersMap))
                      );
                      return new Map(newFiltersMap);
                    });
                  }}
                />
              )}
            {userAbsencePermission &&
              filterColumnsItems.find((i) => +i.id === ABSENCE_ON_OBJECT) && (
                <>
                  <Spacer size={16} />
                  <CustomAbsenceFilter
                    item={{
                      id: "object-absence",
                      columnId: filterColumnsItems.find(
                        (i) => +i.id === ABSENCE_ON_OBJECT
                      )?.id,
                      // @ts-ignore
                      value: mapToFilterList(filterItems).find(
                        (item) => item.id && +item.id === ABSENCE_ON_OBJECT
                      )?.value,
                    }}
                    onChange={(item) => {
                      setFilterItems((filtersMap) => {
                        let list = mapToFilterList(filtersMap);
                        list = list.filter(
                          (listItem) => +listItem.id !== ABSENCE_ON_OBJECT
                        );
                        const newFiltersMap = filterListToMap(list);
                        if (!item.value) {
                          newFiltersMap.delete(item.id);
                        } else {
                          newFiltersMap.set(item.id, item);
                        }
                        setUnsavedCustomFilters?.(
                          toMoreState(mapToFilterList(newFiltersMap))
                        );
                        return new Map(newFiltersMap);
                      });
                    }}
                  />
                </>
              )}
            {!disableDropdowns && (
              <>
                <Spacer size={16} />
                <Divider />
                <Spacer size={16} />
              </>
            )}
            {autoDeactivatedFilter && setAutoDeactivatedFilter && (
              <>
                <AutoDeactivatedAlarmsCheck
                  autoDeactivatedFilter={autoDeactivatedFilter}
                  setAutoDeactivatedFilter={setAutoDeactivatedFilter}
                />
                <Spacer size={16} />
                <Divider />
                <Spacer size={16} />
              </>
            )}

            {numInlineFilterItems > 0 && (
              <>
                {Array.from(filterItems).map(([_, item]) =>
                  renderFilterLine(item)
                )}
              </>
            )}
            {maxFiltersAmount > filterItems.size && !disableDropdowns && (
              <Button
                text={t("Common:custom_filters_add")}
                variant="link"
                iconVariant="primary"
                image="plus"
                onClick={addFilter}
                className={styles.addFilterButton}
                dataCy="add_more_filters_btn"
              />
            )}
            <div>
              <Button
                variant="primary"
                onClick={onApplyFilters}
                text={t("Common:custom_filters_apply")}
                className={styles.applyFilterButton}
                disabled={
                  (filterItems.size === 0 &&
                    filterItems.size === initialFilters.size) ||
                  !!filterItems.entries().some((item) => !item[1].value)
                }
                dataCy="apply_more_filters_btn"
              />
              <Button
                variant="secondary"
                onClick={onCancel}
                text={t("Common:cancel")}
                dataCy="cancel_more_filters_btn"
              />
              <ClearFilter
                text={t("Common:labels_clear_filters")}
                onClearClick={() => {
                  resetMoreFilter();
                  setFilterItems(new Map());
                  setUnsavedCustomFilters?.({});
                  applyFilters(mapToFilterList(new Map()));
                }}
                filterToWatch={
                  (filters?.length || 0) > 0 ? mapToFilterList(filterItems) : {}
                }
                customStyles={{ float: "right", marginTop: "16px" }}
              />
            </div>
          </div>
        </section>
      </OverlayPanel>
    </>
  );
};
